import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import { formatDate, formatDuration } from '../utils/format';
import { t as translate, tt as translateText, route } from '../i18n';

const t = (key, params) => translate(`club.livesets.${key}`, params);
const tt = (key, params) => translateText(`club.livesets.${key}`, params);

// Hoeveel sets per groep zichtbaar zijn voor "toon alles".
const PREVIEW = 10;
const KINDS = ['liveset', 'reunie', 'tribute'];
const ICONS = {
  youtube: 'fab fa-youtube',
  mixcloud: 'fab fa-mixcloud',
  soundcloud: 'fab fa-soundcloud',
  facebook: 'fab fa-facebook',
  archive: 'fas fa-archive',
  hearthis: 'fas fa-headphones',
  mixtapes: 'fas fa-compact-disc',
  other: 'fas fa-headphones',
};
const PLATFORM_NAMES = {
  youtube: 'YouTube',
  mixcloud: 'Mixcloud',
  soundcloud: 'SoundCloud',
  facebook: 'Facebook',
  archive: 'archive.org',
  hearthis: 'hearthis.at',
  mixtapes: 'mixtapes.be',
};
// Vanaf zoveel sets in een groep komt er een jaarfilter.
const YEAR_FILTER = 30;

/** Oudste opname eerst; sets zonder datum achteraan. */
const byDate = (a, b) => (a.date || '9999').localeCompare(b.date || '9999');

/**
 * Sectie "Livesets" op de clubpagina: links naar opnames van DJ-sets uit de club
 * (storage/livesets.json via ClubLivesets.php).
 */
export default class ClubLivesets extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.expanded = {};
    this.year = {};
  }

  view() {
    const data = this.attrs.club.livesets() || {};
    const sets = data.sets || [];
    const collections = data.collections || [];
    if (!sets.length && !collections.length) return null;

    const groups = KINDS.map((kind) => [kind, sets.filter((s) => s.kind === kind).sort(byDate)]).filter(([, list]) => list.length);

    return (
      <section className="LcSection LcLivesets" id="livesets">
        <div className="LcSection-head">
          <h2>{t('title')}</h2>
          {sets.length ? <span className="LcSection-count">{sets.length}</span> : null}
        </div>
        <p className="LcNote">{t('note')}</p>

        {groups.map(([kind, all]) => {
          const years = all.length >= YEAR_FILTER ? this.years(all) : [];
          const year = this.year[kind];
          const list = year ? all.filter((s) => (s.date || '').slice(0, 4) === year) : all;
          const open = this.expanded[kind] || year;

          return (
            <div className="LcLivesetGroup">
              {groups.length > 1 || kind !== 'liveset' ? <h3>{t(`kind.${kind}`)}</h3> : null}
              {years.length > 1 ? (
                <div className="LcLivesetYears" role="group" aria-label={tt('filter_year')}>
                  <button className={'LcYearChip' + (!year ? ' is-active' : '')} onclick={() => (this.year[kind] = null)}>
                    {t('all_years')} <span>{all.length}</span>
                  </button>
                  {years.map(([y, n]) => (
                    <button className={'LcYearChip' + (year === y ? ' is-active' : '')} onclick={() => (this.year[kind] = year === y ? null : y)}>
                      {y} <span>{n}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              <ol className="LcLivesetList">{(open ? list : list.slice(0, PREVIEW)).map((s) => this.item(s))}</ol>
              {!year && list.length > PREVIEW ? (
                <Button
                  className="Button LcMore"
                  icon={this.expanded[kind] ? 'fas fa-chevron-up' : 'fas fa-list'}
                  onclick={() => (this.expanded[kind] = !this.expanded[kind])}
                >
                  {this.expanded[kind] ? translate('club.show_less') : translate('club.show_all', { count: list.length })}
                </Button>
              ) : null}
            </div>
          );
        })}

        {collections.length ? (
          <div className="LcLivesetGroup">
            <h3>{t('collections')}</h3>
            <ul className="LcLivesetCollections">
              {collections.map((c) => (
                <li>
                  <a href={c.url} target="_blank" rel="noopener noreferrer nofollow">
                    <i className={ICONS[c.platform] || ICONS.other} aria-hidden="true" />
                    <span>
                      <strong>{c.title}</strong>
                      <small>{[c.count ? t('count', { count: c.count }) : null, c.note, PLATFORM_NAMES[c.platform]].filter(Boolean).join(' · ')}</small>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    );
  }

  /** [[jaar, aantal], ...] van oud naar nieuw; sets zonder datum tellen niet mee. */
  years(list) {
    const count = {};
    list.forEach((s) => {
      const y = (s.date || '').slice(0, 4);
      if (y) count[y] = (count[y] || 0) + 1;
    });
    return Object.keys(count)
      .sort()
      .map((y) => [y, count[y]]);
  }

  item(s) {
    const meta = [s.date ? formatDate(s.date) : null, s.duration ? formatDuration(s.duration) : null].filter(Boolean).join(' · ');
    const platform = this.platformName(s);
    const extra = [
      s.tracklist ? (
        <a href={s.tracklist} target="_blank" rel="noopener noreferrer nofollow">
          {t('tracklist')}
        </a>
      ) : null,
      ...(s.alt || []).map((a) => (
        <a href={a.url} target="_blank" rel="noopener noreferrer nofollow">
          {t('also_on', { platform: this.platformName(a) })}
        </a>
      )),
    ].filter(Boolean);

    return (
      <li className={`LcLiveset LcLiveset--${s.platform}`}>
        <a className="LcLiveset-play" href={s.url} target="_blank" rel="noopener noreferrer nofollow" aria-label={tt('listen', { title: s.title, platform })}>
          <i className={ICONS[s.platform] || ICONS.other} aria-hidden="true" />
        </a>
        <div className="LcLiveset-body">
          <div className="LcLiveset-head">
            <strong>{s.dj ? this.djName(s) : s.event || t('unknown_dj')}</strong>
            {meta ? <span className="LcLiveset-meta">{meta}</span> : null}
          </div>
          {s.dj && s.event ? <span className="LcLiveset-event">{s.event}</span> : null}
          <a className="LcLiveset-title" href={s.url} target="_blank" rel="noopener noreferrer nofollow">
            {s.title}
            <span className="LcLiveset-platform"> · {platform}</span>
          </a>
          {s.note ? <span className="LcLiveset-note">{s.note}</span> : null}
          {extra.length ? <span className="LcLiveset-links">{extra.map((a, i) => [i ? ' · ' : null, a])}</span> : null}
        </div>
      </li>
    );
  }

  platformName(s) {
    if (PLATFORM_NAMES[s.platform]) return PLATFORM_NAMES[s.platform];
    try {
      return new URL(s.url).hostname.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  /** DJ-naam; gekende artiesten worden links naar hun pagina. */
  djName(s) {
    const parts = s.dj_parts || [];
    if (!parts.some((p) => p.slug)) return s.dj;
    return parts.map((p) => (p.slug ? <Link href={route('lc.artist', { slug: p.slug })}>{p.text}</Link> : p.text));
  }
}
